import React, { ForwardRefRenderFunction } from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { closeDialog } from "store/actions/utilActions";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { QuotationPreviewProps } from "models/propTypes/quotationPropTypes";
import { DialogContentHandles } from "models/propTypes/dialogPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import {
  PageFooter,
  SimpleEntityTitle,
  ViewPanel,
  FormValidity,
  WidgetTitle,
  useFloatingMessages,
} from "components/common";
import { InvoiceContextProvider, InvoiceTemplate } from "components/invoice";
import QuotationPreviewFormContainer from "./forms/previewFormContainer";
import QuotationDetails from "./details";
import { useQuotesService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const QuotationPreview: ForwardRefRenderFunction<DialogContentHandles, QuotationPreviewProps> = (props, ref) => {
  const quotesService = useQuotesService();
  const { quotationPost, reloadQuotation, serialNumber, status, customerName } = props;
  const formRef = React.useRef<FormHandles>(null);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const [pendingReloadId, setPendingReloadId] = React.useState<number>();
  const [errorOnSubmit, setErrorOnSubmit] = React.useState<boolean>(false);

  const { enqueueMessage } = useFloatingMessages();

  const serialNumberTemplateValueDisplayText = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Quotation.QuotationNumberHashTag
  );

  React.useImperativeHandle(ref, () => ({
    onClose: onClosePreview,
  }));

  const onSaveAsDraftClick = async () => {
    const values: InvoiceFormTypes.QuotationPreview = formRef.current?.getValues && formRef.current?.getValues();
    if (quotationPost) {
      let id = serialNumber ?? pendingReloadId;
      try {
        if (errorOnSubmit) {
          setErrorOnSubmit(false);
        }

        id = await quotesService.createOrUpdateQuotation(id, {
          ...quotationPost,
          notes: values.notes,
          attachments: values.attachments as WintSuperkollAPIModel.QuotationAttachment[],
        });

        if (id) {
          setPendingReloadId(id);
        }
      } catch (error) {
        setErrorOnSubmit(true);
        setPendingReloadId(id);

        throw error;
      }
    }
  };

  const onSendClick = async (values: InvoiceFormTypes.QuotationPreview) => {
    let updatedSerialNumber = serialNumber ?? pendingReloadId;
    try {
      if (errorOnSubmit) {
        setErrorOnSubmit(false);
      }

      if (quotationPost) {
        updatedSerialNumber = await quotesService.createOrUpdateQuotation(serialNumber, {
          ...quotationPost,
          notes: values.notes,
          attachments: values.attachments as WintSuperkollAPIModel.QuotationAttachment[],
        });
      }

      if (updatedSerialNumber) {
        switch (values.sendType) {
          case WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent:
            if (status === WintSuperkollAPIModel.QuotationApiState.NotSent)
              await quotesService.markAsSent(updatedSerialNumber);
            await quotesService.downloadPdf(updatedSerialNumber);
            break;
          case WintSuperkollAPIModel.InvoiceSendMethod.Email:
            await quotesService.sendAsEmail({
              serialNumber: updatedSerialNumber,
              mailFrom: values.sendMail?.emailFromType ?? 0,
              mailToType: getEmailToType(values.sendMail?.emailTo), // replace with InvoiceHelper.getEmailToType when converting to new form
              mailSubject: values.sendMail?.emailSubject?.replace(
                serialNumberTemplateValueDisplayText,
                updatedSerialNumber.toString()
              ),
              mailToAddresses:
                values.sendMail?.emailTo &&
                values.sendMail.emailTo
                  .map((e) => e.value)
                  .filter((email) => email)
                  .join(","),
              mailMessage: values.sendMail?.emailMessage,
            });
        }

        closePreview(updatedSerialNumber, true);
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(
            values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.Email
              ? TranslationKeys.Quotation.SendMailSuccessMessage
              : TranslationKeys.Quotation.MarkedAsSentSuccessMessage
          ),
          StyleEnums.DisplayType.Success
        );
      }
    } catch (error) {
      setErrorOnSubmit(true);
      setPendingReloadId(updatedSerialNumber);
    }
  };

  const onSubmit = (submitFunc: (values: any) => Promise<void>) => {
    return (formRef.current && formRef.current.submit(submitFunc)) || new Promise<void>((resolve) => resolve());
  };

  const onClosePreview = (id?: number, markAsSent?: boolean) => {
    const toReloadId = id ?? pendingReloadId;

    if (
      toReloadId &&
      reloadQuotation &&
      ((serialNumber === undefined && pendingReloadId !== undefined) || !errorOnSubmit)
    ) {
      reloadQuotation(toReloadId);
    }

    if (errorOnSubmit) {
      setErrorOnSubmit(false);
    }
  };

  const closePreview = (id?: number, markAsSent?: boolean) => {
    onClosePreview(id, markAsSent);
    dispatch(closeDialog());
  };

  const getEmailToType = (emailTo?: AutocompleteListItem[]): WintSuperkollAPIModel.SendInvoiceMailsToEnum => {
    if (emailTo && emailTo.length === 1) {
      const type = emailTo[0].id?.toString().split("_")[0];
      const typeAsSendInvoiceMailsToEnum = Number(type) as WintSuperkollAPIModel.SendInvoiceMailsToEnum;
      return typeAsSendInvoiceMailsToEnum
        ? typeAsSendInvoiceMailsToEnum
        : WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other;
    }
    return WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other;
  };

  return (
    <FormValidity
      render={(isValid, onValidityChange) => (
        <InvoiceContextProvider>
          <Grid container className={clsx(cssExports.entityCreationLayout, cssExports.invoicePreviewContainer)}>
            <Grid container direction="column" className={cssExports.invoicePreviewContainer}>
              <Grid item container spacing={4} className={cssExports.upperContainer}>
                <Grid item container md={7}>
                  <SimpleEntityTitle
                    title={`${LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Quotation.QuotationTo
                    )} ${customerName}`}
                    status={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ToReview)}
                  />
                </Grid>
                <Grid item container md={5} direction="column" justifyContent="flex-end"></Grid>
              </Grid>
              <Grid item container spacing={4}>
                <Grid item container md={7} className={cssExports.lowerLeftContainer}>
                  <InvoiceTemplate />
                </Grid>
                <Grid
                  item
                  container
                  md={5}
                  direction="column"
                  className={cssExports.viewPanelItemContainer}
                  justifyContent="space-between"
                >
                  <ViewPanel>
                    <WidgetTitle>
                      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReviewMessage)}
                    </WidgetTitle>
                    <QuotationDetails
                      total={props.total}
                      tax={props.tax}
                      currency={props.currency}
                      referencePersonName={props.referencePersonName}
                      dimensions={quotationPost?.dimensions}
                    />
                    <QuotationPreviewFormContainer
                      ref={formRef}
                      {...props}
                      onFormValidationChange={onValidityChange}
                      isDraft={status === WintSuperkollAPIModel.QuotationApiState.NotSent}
                    />
                  </ViewPanel>
                </Grid>
              </Grid>
            </Grid>
            <PageFooter
              showNavigateBack
              actions={(status === WintSuperkollAPIModel.QuotationApiState.NotSent
                ? [
                    {
                      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveDraft),
                      onClick: onSaveAsDraftClick,
                      type: StyleEnums.DisplayType.Info,
                      isDisabled: false,
                      successMessage: TranslationKeys.Quotation.SaveSuccessMessage,
                    },
                  ]
                : []
              ).concat([
                {
                  text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Send),
                  onClick: onSubmit.bind(null, onSendClick),
                  type: StyleEnums.DisplayType.Success,
                  isDisabled: !isValid,
                  successMessage: "",
                },
              ])}
              onBackButtonClick={closePreview.bind(null, undefined, undefined)}
              columnsLeft={7}
            />
          </Grid>
        </InvoiceContextProvider>
      )}
    />
  );
};

export default React.forwardRef(QuotationPreview);
